import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { trackVisit } from '@/composables/useVisitTracking'

/**
 * Aplikace má dvě oddělené sekce:
 *   /        — uživatelská část, dostupná každému přihlášenému
 *   /admin/* — administrace, vyžaduje ROLE_ADMIN
 *
 * Router jen skrývá to, k čemu uživatel nemá přístup; skutečná hranice
 * je vynucená na API (viz access_control v security.yaml).
 */
const routes: RouteRecordRaw[] = [
  // --- veřejné ---
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true, layout: 'auth', title: 'Přihlášení' },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: { public: true, layout: 'auth', title: 'Zapomenuté heslo' },
  },
  {
    path: '/reset-password/:token',
    name: 'reset-password',
    component: () => import('@/views/ResetPasswordView.vue'),
    props: true,
    meta: { public: true, layout: 'auth', title: 'Nové heslo' },
  },

  // Souhlas s připojením AI konektoru (OAuth). Vyžaduje přihlášení — guard
  // nepřihlášeného pošle na login s `redirect` včetně celého query stringu
  // autorizačního požadavku. Vzhled jednoduchý, bez postranního menu.
  {
    path: '/oauth/consent',
    name: 'oauth-consent',
    component: () => import('@/views/OauthConsentView.vue'),
    meta: { layout: 'auth', title: 'Souhlas s připojením' },
  },

  // --- uživatelská sekce ---
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/user/HomeView.vue'),
    meta: { section: 'user', title: 'Přehled' },
  },
  {
    path: '/offers',
    name: 'offers',
    component: () => import('@/views/user/OffersView.vue'),
    meta: { section: 'user', title: 'Nabídky' },
  },
  {
    path: '/cheapest',
    name: 'cheapest',
    component: () => import('@/views/user/CheapestView.vue'),
    meta: { section: 'user', title: 'Nejlevnější' },
  },
  {
    path: '/products/:id(\\d+)',
    name: 'product',
    component: () => import('@/views/user/ProductDetailView.vue'),
    props: true,
    meta: { section: 'user', title: 'Historie cen' },
  },
  {
    path: '/compare',
    name: 'compare',
    component: () => import('@/views/user/CompareView.vue'),
    meta: { section: 'user', title: 'Srovnání' },
  },
  {
    path: '/basket',
    name: 'basket',
    component: () => import('@/views/user/BasketView.vue'),
    meta: { section: 'user', title: 'Košík' },
  },
  {
    path: '/planner',
    name: 'planner',
    component: () => import('@/views/user/PlannerView.vue'),
    meta: { section: 'user', title: 'Plánovač nákupu' },
  },
  {
    path: '/map',
    name: 'map',
    component: () => import('@/views/user/MapView.vue'),
    meta: { section: 'user', title: 'Mapa prodejen' },
  },
  {
    path: '/leaflets',
    name: 'leaflets',
    component: () => import('@/views/user/LeafletsView.vue'),
    meta: { section: 'user', title: 'Letáky' },
  },
  {
    path: '/leaflets/:id(\\d+)',
    name: 'leaflet',
    component: () => import('@/views/user/LeafletDetailView.vue'),
    props: true,
    meta: { section: 'user', title: 'Detail letáku' },
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: () => import('@/views/user/NotificationsView.vue'),
    meta: { section: 'user', title: 'Notifikace' },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/user/SettingsView.vue'),
    meta: { section: 'user', title: 'Nastavení účtu' },
  },
  {
    path: '/settings/mcp',
    name: 'settings-mcp',
    component: () => import('@/views/user/McpSettingsView.vue'),
    meta: { section: 'user', title: 'Připojení AI (MCP)' },
  },
  {
    path: '/components',
    name: 'components',
    component: () => import('@/views/ComponentsView.vue'),
    meta: { section: 'user', title: 'Komponenty' },
  },

  // --- administrace ---
  {
    path: '/admin',
    name: 'admin-dashboard',
    component: () => import('@/views/admin/AdminDashboardView.vue'),
    meta: { section: 'admin', role: 'ROLE_ADMIN', title: 'Přehled aplikace' },
  },
  {
    path: '/admin/users',
    name: 'admin-users',
    component: () => import('@/views/admin/UsersView.vue'),
    meta: { section: 'admin', role: 'ROLE_ADMIN', title: 'Uživatelé' },
  },
  {
    path: '/admin/users/import',
    name: 'admin-users-import',
    component: () => import('@/views/admin/UserImportView.vue'),
    meta: { section: 'admin', role: 'ROLE_ADMIN', title: 'Import uživatelů' },
  },
  {
    path: '/admin/visits',
    name: 'admin-visits',
    component: () => import('@/views/admin/VisitsView.vue'),
    meta: { section: 'admin', role: 'ROLE_ADMIN', title: 'Návštěvnost' },
  },

  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true, title: 'Stránka nenalezena' },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (_to, _from, savedPosition) => savedPosition ?? { top: 0 },
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  // Při tvrdém načtení stránky ještě nevíme, jestli cookie session platí.
  if (!auth.initialized) {
    await auth.loadProfile()
  }

  if (to.meta.public) {
    // Přihlášenému nemá smysl ukazovat login.
    if (auth.isAuthenticated && (to.name === 'login' || to.name === 'forgot-password')) {
      return { name: 'home' }
    }
    return true
  }

  if (!auth.isAuthenticated) {
    return { name: 'login', query: to.fullPath !== '/' ? { redirect: to.fullPath } : undefined }
  }

  const requiredRole = to.meta.role
  if (typeof requiredRole === 'string' && !auth.hasRole(requiredRole)) {
    // Bez oprávnění se uživatel vrací do své vlastní sekce.
    return { name: 'home' }
  }

  return true
})

router.afterEach((to) => {
  const title = typeof to.meta.title === 'string' ? to.meta.title : null
  document.title = title ? `${title} — Lets` : 'Lets'

  // Návštěvnost se hlásí na pozadí; selhání nesmí ovlivnit navigaci.
  if (!to.meta.public && typeof to.name === 'string') {
    trackVisit(to.name)
  }
})
